/**
 * sl-k94y.5.1: Locale settings React hook.
 *
 * Reads `availableLocales` + `defaultLocale` from the deployment via the
 * `localeSettings` GraphQL query. Result is what the render layer feeds
 * into `pickDescription` / `pickTitle` so the locale-pick fallback chain
 * matches the deployment's configured baseline (instead of a hard-coded
 * `en` that was implicit before locales became DB-driven).
 *
 * Suspense is opt-OUT explicitly — the marketplace render path needs
 * loading-aware UI (skeletons), not a Suspense boundary that would block
 * the entire surrounding tree on a 60s-cached settings fetch.
 *
 * Cache layer: SWR sits on top of the GraphQL fetcher; the backend
 * `LocaleSettingsService` adds its own 60s in-process cache. Together
 * they keep the query effectively free for repeat callers within a tab.
 */
import { useQuery } from '@affine/core/components/hooks/use-query';
import { localeSettingsQuery } from '@affine/graphql';
import { useMemo } from 'react';

export interface LocaleSettings {
  availableLocales: ReadonlyArray<string>;
  defaultLocale: string;
}

export interface UseLocaleSettingsResult {
  settings: LocaleSettings | null;
  isLoading: boolean;
  error: Error | null;
}

export function useLocaleSettings(): UseLocaleSettingsResult {
  const { data, error, isLoading } = useQuery(
    { query: localeSettingsQuery },
    { suspense: false }
  );

  return useMemo<UseLocaleSettingsResult>(() => {
    if (!data?.localeSettings) {
      return {
        settings: null,
        isLoading,
        error: error ?? null,
      };
    }
    return {
      settings: {
        availableLocales: data.localeSettings.availableLocales,
        defaultLocale: data.localeSettings.defaultLocale,
      },
      isLoading,
      error: error ?? null,
    };
  }, [data, error, isLoading]);
}
